import Vue from 'vue';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import VueAxios from 'vue-axios';
import jwtDecode from 'jwt-decode';

import { app } from '@/main';
import { treatError } from '@/utils/index';
import eventBus from '@/utils/eventBus';
import segmentIo, {
  shouldShowTrackingDebugger,
  trackingDebuggerIsActive
} from '@/utils/segment.io';
import { sendApiEventToHotjar } from '@/utils/hotjar';
import { USER_TIMEZONE } from '@/utils/DateUtils';

import ModalOptions from '@/components/Modal/ModalOptions';

import { IQueryPaginationOptions } from '@/models/general';
import { IHotjarEventType } from '@/models/hotjar-options';
import HTTPStatus from '@/constants/httpStatus';
import MaintenanceKeys from '@/constants/maintenance';
import logger from '@/utils/logger';
import { datadogLogs } from '@datadog/browser-logs';

axios.defaults.withCredentials = true;

interface APIServiceConfig {
  disableToastMessage?: boolean;
}

const hostname = window.location.hostname;
const domain = hostname.includes('twism.com')
  ? '.twism.com'
  : hostname.includes('twism.info')
  ? '.twism.info'
  : '';

const removeAppCookieWithAllDomains = () => {
  // Remove cookie with no domain (current subdomain only)
  document.cookie = `app=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

  // Remove cookie with parent domain
  if (domain) {
    document.cookie = `app=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  // Remove cookie with current hostname
  document.cookie = `app=; path=/; domain=${hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const setAppCookie = (appType: 'dashboard' | 'instore') => {
  removeAppCookieWithAllDomains();
  document.cookie = `app=${appType}; path=/; domain=${domain}`;
};

class APIService {
  static TOKEN_KEY: string = 'access_token';
  static SUPER_ADMIN: string = 'token';
  static PLATFORM_KEY = 'dashboard';

  static token: string = '';
  axios: AxiosInstance;
  interceptor!: number;

  constructor(private config?: APIServiceConfig) {
    Vue.use(VueAxios, axios);
    this.axios = axios.create();
    this.setEventBrowserDataOnHeader();
    this.setHeader();
    this.initInterception();
  }

  host() {
    return process.env.VUE_APP_API_URL;
  }

  servicesHost() {
    return process.env.VUE_APP_SERVICES_URL;
  }

  campaignHost() {
    return process.env.VUE_APP_CAMPAIGN_URL;
  }

  dashboardHost() {
    return process.env.VUE_APP_DASHBOARD_URL;
  }

  recaptchaSiteKey() {
    return process.env.VUE_APP_RECAPTCHA_SITE_KEY;
  }

  setEventBrowserDataOnHeader() {
    (async () => {
      const utmData = segmentIo.utmProperties();
      const deviceInfos = await segmentIo.getDeviceInfos();

      const eventBrowserData = {
        ...deviceInfos,
        ...utmData
      };

      Vue.axios.defaults.headers.common['event-browser-data'] = JSON.stringify(
        eventBrowserData
      );
    })();
  }

  setHeader() {
    Vue.axios.defaults.headers.common['event-platform'] =
      APIService.PLATFORM_KEY;
    Vue.axios.defaults.headers.common['timezone'] = USER_TIMEZONE;
    Vue.axios.defaults.headers.common[
      'timezone-offset'
    ] = new Date().getTimezoneOffset();

    if (shouldShowTrackingDebugger())
      Vue.axios.defaults.headers.common[
        'debugger-event'
      ] = trackingDebuggerIsActive();

    if (APIService.token) {
      Vue.axios.defaults.headers.common.Authorization = `Bearer ${
        APIService.token
      }`;
    } else {
      delete Vue.axios.defaults.headers.common.Authorization;
    }
  }

  getHeader() {
    return `Bearer ${APIService.token}`;
  }

  disableInterception() {
    this.axios.interceptors.response.eject(this.interceptor);
  }

  get servicesDisabledRouteErrors() {
    return [
      '/business/signup/pre-validation',
      '/admin/customer-lead/wix/eligible-customers',
      '/auth/signin',
      '/manager/forgot-password/',
      '/admin/business-coin-setup/reserve',
      '/business/signup',
      '/v2/admin/business/sell-coins-settings',
      '/v2/location/suggestion-name/',
      '/admin/business-lead',
      '/smart-business-signup/offer-recommendation',
      '/smart-business-signup/generate-branded-coins'
    ];
  }

  get shouldDisableToastMessage() {
    return this.config && this.config.disableToastMessage;
  }

  initInterception() {
    this.interceptor = this.axios.interceptors.response.use(
      response => {
        const { hostname, pathname } = new URL(response.config.url || '');

        sendApiEventToHotjar({
          methodRequest: response.config.method!,
          typeEvent: IHotjarEventType.SUCCESS,
          hostApiRequest: hostname,
          pathnameApiRequest: pathname
        });

        return response;
      },
      async error => {
        if (
          error.response &&
          error.response.status === HTTPStatus.SERVICE_UNAVAILABLE &&
          error.response.data
        ) {
          app.$router.push('/maintenance');

          localStorage.setItem(
            MaintenanceKeys.LAST_ROUTE_BEFORE_MAINTENANCE_STORAGE_KEY,
            app.$route.path
          );

          return {
            status: HTTPStatus.SERVICE_UNAVAILABLE
          };
        }

        const config = error.config || {};

        // Set the variable for the maximum number of retries
        config.retryCount = config.retryCount || 0;
        const maxRetryCount = (config.retry || {}).maxRetryCount || 2;

        if (
          (error.message && error.message.includes('timeout')) ||
          (error.message && error.message.includes('Network Error')) ||
          (error.response &&
            (error.response.status === 502 ||
              error.response.status === 503 ||
              error.response.status === 504) &&
            config.retryCount < maxRetryCount)
        ) {
          datadogLogs.logger.info(
            `Message: ${error.message}; status: ${
              error.response ? error.response.status : ''
            };`,
            {},
            error
          );
          // Increase the retry count
          config.retryCount += 1;
          // Set the delay between retries using the backoff strategy
          const delay = (config.retry || {}).delay || 1300;
          const backoff = (config.retry || {}).backoff || 2;
          const delayTime = delay * Math.pow(backoff, config.retryCount);

          return new Promise(resolve => {
            setTimeout(() => {
              resolve(this.axios(config));
            }, delayTime);
          });
        }

        const { hostname, pathname } = new URL(error.config.url || '');

        if (pathname.match(/\/analytics\/|\/bi\//)) {
          return null;
        }

        if (pathname.match(/\/oauth\/api-token\//)) {
          throw error;
        }

        const sholdNotShowError = this.servicesDisabledRouteErrors.some(path =>
          pathname.includes(path)
        );

        if (!error.response) {
          return null;
        }

        sendApiEventToHotjar({
          methodRequest: error.response.config.method,
          typeEvent: IHotjarEventType.ERROR,
          hostApiRequest: hostname,
          pathnameApiRequest: pathname
        });

        const execTreatError = (error: any) => {
          if (sholdNotShowError || this.shouldDisableToastMessage) return;

          treatError(error);
        };

        if (
          error.response &&
          error.response.status === HTTPStatus.UNAUTHORIZED &&
          !error.config.url.includes('auth/signin')
        ) {
          const jwtDecoded = jwtDecode(APIService.token);
          const permissions = jwtDecoded.extraInfo
            ? jwtDecoded.extraInfo.permissions ||
              (jwtDecoded.extraInfo.impersonator || {}).permissions ||
              []
            : [];

          // TODO: fix services/analytics error
          if (
            window.location.hostname === 'localhost' ||
            permissions.includes('read-only')
          ) {
            execTreatError(error);
            return error;
          }

          execTreatError(error);
          APIService.resetCredentials();

          return error;
        } else if (
          error.response &&
          error.response.status === HTTPStatus.FORBIDDEN
        ) {
          if (pathname.match(/\/admin\/special-offer\//)) {
            window.location.assign('/special-coins-offers');
            return;
          }
          app.$showLoaderWithLocker();
          try {
            const res = await this.get('/admin/business/connect');
            if (res.data[0]) {
              app.$hideLoader();
              eventBus.$emit(
                'openSubModal',
                new ModalOptions(
                  ``,
                  ``,
                  'none',
                  true,
                  ``,
                  ``,
                  ``,
                  false,
                  'LostAccessModal',
                  false,
                  true,
                  true
                ),
                { data: res.data[0] }
              );
            } else {
              if (location.pathname !== '/manager')
                window.location.assign('/manager');
            }
          } catch (e) {
            APIService.resetCredentials();
          } finally {
            app.$hideLoader();
          }

          return null;
        }

        execTreatError(error);
        return error;
      }
    );
  }

  public static isSuperAdmin() {
    return window.location.pathname.includes('/admin');
  }

  public static readCredentials(isSuperAdmin?: boolean) {
    let token: string | null = '';

    const superAdmin = localStorage.getItem(
      `${window.location.host}-${APIService.SUPER_ADMIN}`
    );

    if (superAdmin && jwtDecode(superAdmin).extraInfo.isImpersonated) {
      token = superAdmin;
    } else {
      token = isSuperAdmin
        ? superAdmin
        : localStorage.getItem(
            `${window.location.host}-${APIService.TOKEN_KEY}`
          );
    }

    APIService.token = token || '';
  }

  removeEmpty(obj: any) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != ''));
  }

  public static redirectToSignin() {
    const signinPath = this.isSuperAdmin() ? '/admin' : '/';
    window.location.assign(signinPath);
  }

  public static resetCredentials(callback?: () => void) {
    segmentIo.reset();
    logger.reset();

    localStorage.removeItem(
      this.isSuperAdmin()
        ? `${window.location.host}-${APIService.SUPER_ADMIN}`
        : `${window.location.host}-${APIService.TOKEN_KEY}`
    );

    APIService.token = '';
    setAppCookie('dashboard');

    if (callback) {
      return callback();
    }

    return this.redirectToSignin();
  }

  setCredentials(token: string, isSuperAdmin?: boolean) {
    if (isSuperAdmin) {
      localStorage.setItem(
        `${window.location.host}-${APIService.SUPER_ADMIN}`,
        token
      );
    } else {
      localStorage.setItem(
        `${window.location.host}-${APIService.TOKEN_KEY}`,
        token
      );
    }

    APIService.token = token;

    this.setHeader();
  }

  getCredentials(isSuperAdmin?: boolean) {
    if (isSuperAdmin) {
      return localStorage.getItem(
        `${window.location.host}-${APIService.SUPER_ADMIN}`
      );
    }
    return localStorage.getItem(
      `${window.location.host}-${APIService.TOKEN_KEY}`
    );
  }

  static getManagerJWTDecoded() {
    const managerJwt = localStorage.getItem(
      `${window.location.host}-${APIService.TOKEN_KEY}`
    );

    if (managerJwt) {
      return jwtDecode(managerJwt);
    }
  }

  get<T = any>(resource: string, config?: AxiosRequestConfig) {
    return this.axios.get<T>(this.host() + resource, config);
  }

  post<T = any>(resource: string, params?: object, headers?: object) {
    return this.axios.post<T>(this.host() + resource, params, headers);
  }

  put<T = any>(resource: string, params?: object) {
    return this.axios.put<T>(this.host() + resource, params);
  }

  patch(resource: string, params: object) {
    return this.axios.patch(this.host() + resource, params);
  }

  delete(resource: string, params?: object) {
    const config = params ? { data: params } : undefined;
    return this.axios.delete(this.host() + resource, config);
  }

  public generateResource(
    path: string,
    sortField: string,
    sortOrder: string = '',
    pagination: string = '',
    filters?: string,
    hasOldPagination?: boolean,
    defaultFilter?: string
  ): string {
    let resource = path;
    if (pagination.includes(resource)) resource = pagination;
    else resource += pagination;

    if (sortField && !pagination.includes('sort'))
      resource += `sort=${sortField}`;

    if (hasOldPagination) {
      if (sortOrder && !pagination.includes('order'))
        resource += `&order=${sortOrder}`;
    } else if (sortOrder && !pagination.includes(':')) {
      resource += `:${sortOrder}`;
    }

    if (
      filters &&
      !pagination.includes('search') &&
      !pagination.includes('extras') &&
      !pagination.includes('filters')
    ) {
      resource += filters;
    }

    if (defaultFilter && !resource.includes(defaultFilter)) {
      resource += defaultFilter;
    }

    return resource;
  }

  public dynamicResource(data: any) {
    let attr = '';
    for (var key in data) {
      attr += `&${key}=${data[key]}`;
    }
    return attr;
  }

  public getQueryString(urlOrRecord: Record<string, string> | string): string {
    if (typeof urlOrRecord === 'string') {
      const query = urlOrRecord.split('?').pop(); // removes path
      return new URLSearchParams(query).toString();
    }
    return new URLSearchParams(urlOrRecord).toString();
  }

  public getQueryPagination<T = any>(
    url: string,
    {
      sortField = '',
      sortOrder = '',
      pagination = '',
      filters,
      period
    }: Partial<IQueryPaginationOptions>
  ) {
    let resource = this.generateResource(
      `${url}?`,
      sortField,
      sortOrder,
      pagination,
      filters
    );

    if (period && !resource.includes('from')) {
      resource += this.dynamicResource(period);
    }

    return this.get<T>(resource);
  }
}

export default APIService;
